import mergeObjects from '@bybeam/platform-lib/answers/mergeObjects';
import {
  ApplicationConfig,
  ApplicationQuestion,
  ApplicationSection,
  CommonQuestionKeys,
  CompareOperator,
  ExpressionType,
  Field,
  FieldType,
  FieldValidation,
  NHCustomQuestionKeys,
  TextInputType,
} from '@bybeam/platform-types';
import {
  RadioButtonOptions,
  YesNoOptions,
  agreements,
  ethnicities,
  genders,
  races,
  utilityTypes,
} from '@platform-ui/application/utils/index.js';
import { and, answer, empty, eq, gte, inList, literal, multiply } from '../../ast/utils.js';
import STANDARD_COMPONENTS from '../standards/index.js';
import { SpecialSectionKeys } from '../standards/types.js';

const DOCUMENT_VALIDATION: FieldValidation = {
  required: true,
  condition: and(
    ['residency', 'income', 'rentAmount', 'utilityBills', 'internetBills'].map((key) =>
      empty(`documents.${key}`),
    ),
  ),
};

type ExtractedCommonQuestionKeys = Extract<
  CommonQuestionKeys,
  | CommonQuestionKeys.ADDRESS
  | CommonQuestionKeys.MAILING_ADDRESS
  | CommonQuestionKeys.ADMIN_ONLY
  | CommonQuestionKeys.DATE_OF_BIRTH
  | CommonQuestionKeys.DEMOGRAPHICS
  | CommonQuestionKeys.HOUSEHOLD_SIZE
  | CommonQuestionKeys.HOUSEHOLD_MEMBERS
  | CommonQuestionKeys.SIGNATURE
  | CommonQuestionKeys.APPLICATION_ASSISTANCE_HELPER
>;

export const QUESTIONS: {
  [key in ExtractedCommonQuestionKeys | NHCustomQuestionKeys]: ApplicationQuestion;
} = {
  // Address & Landlord Section
  [CommonQuestionKeys.ADDRESS]: {
    key: CommonQuestionKeys.ADDRESS,
    copy: {
      title: 'What’s your address?',
      intro:
        'If you’re currently experiencing homelessness, please select the checkbox below. This does not affect eligibility.',
    },
    fields: [
      {
        type: FieldType.Address,
        key: CommonQuestionKeys.ADDRESS,
        filter: { state: 'NH' },
        allowUnhoused: true,
      },
    ],
  },
  [CommonQuestionKeys.MAILING_ADDRESS]: {
    key: CommonQuestionKeys.MAILING_ADDRESS,
    copy: { title: 'What’s your mailing address?' },
    fields: [
      {
        type: FieldType.Address,
        key: CommonQuestionKeys.MAILING_ADDRESS,
        copyAddressKey: CommonQuestionKeys.ADDRESS,
      },
    ],
  },
  [NHCustomQuestionKeys.LANDLORD]: {
    key: NHCustomQuestionKeys.LANDLORD,
    copy: {
      title: 'What’s your landlord’s contact information?',
      intro:
        'Provide all information you have on your landlord and SNHS will contact your landlord.',
    },
    fields: [
      {
        type: FieldType.Typography,
        key: 'address.landlord.contactHeader',
        copy: 'Landlord Contact Information',
        props: { variant: 'h3' },
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'address.landlord.contactName',
        copy: 'Primary Contact Name',
        props: {
          tooltip:
            'Please enter the full name of the person you correspond with about the place you rent. This typically is a landlord or a property manager.',
        },
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'address.landlord.companyName',
        copy: 'Landlord Company Name (Optional)',
        props: {
          tooltip:
            'This is the name of the real estate company that owns your building, if applicable.',
        },
        validation: { required: false },
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Phone,
        key: 'address.landlord.phone',
        copy: 'Primary Contact Phone Number',
        validation: { required: false },
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Email,
        key: 'address.landlord.email',
        copy: 'Primary Contact Email Address',
        validation: { required: false },
      },
      {
        type: FieldType.Typography,
        key: 'address.landlord.addressHeader.tenant',
        copy: 'Landlord Mailing Address',
        props: { variant: 'h3' },
      },
      {
        type: FieldType.Address,
        key: 'address.landlordMailingAddress',
        copy: 'Landlord Mailing Address',
        validation: { required: false },
      },
    ],
  },
  // Admin Only Section
  [CommonQuestionKeys.ADMIN_ONLY]: {
    key: CommonQuestionKeys.ADMIN_ONLY,
    copy: { title: null },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Currency,
        copy: 'Verified Monthly Rent Amount',
        key: 'adminOnly.verifiedRent',
        validation: { required: false },
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Currency,
        copy: 'Verified Rent Arrearage',
        key: 'adminOnly.verifiedArrearage',
        validation: { required: false },
      },
      {
        type: FieldType.Dropdown,
        copy: 'Household Income Source',
        key: 'adminOnly.hasIncomeDocs',
        options: [
          { label: 'Self-Attested', value: 'selfAttested' },
          { label: 'Documentation provided', value: 'documentationProvided' },
        ],
      },
      {
        type: FieldType.Dropdown,
        copy: 'Public Benefits Docs',
        key: 'adminOnly.hasBenefitsDocs',
        validation: { required: false },
        options: [
          { label: 'Documentation provided', value: 'documentationProvided' },
          { label: 'No Documentation provided', value: 'noDocumentationProvided' },
        ],
      },
    ],
  },
  // Personal Info Section
  [CommonQuestionKeys.DATE_OF_BIRTH]: {
    key: CommonQuestionKeys.DATE_OF_BIRTH,
    copy: { title: 'What’s your date of birth?' },
    fields: [
      {
        type: FieldType.Date,
        copy: 'Date of Birth',
        key: CommonQuestionKeys.DATE_OF_BIRTH,
        validation: { required: true, allowedRange: 'past' },
        props: { helperText: 'Example: 11/04/1998' },
      },
    ],
  },
  [CommonQuestionKeys.DEMOGRAPHICS]: {
    key: CommonQuestionKeys.DEMOGRAPHICS,
    copy: { title: 'Tell us a bit about yourself' },
    fields: [
      {
        type: FieldType.Dropdown,
        copy: 'Gender',
        key: 'demographics.gender',
        options: genders,
      },
      {
        type: FieldType.Dropdown,
        copy: 'Race',
        key: 'demographics.race',
        options: races,
        props: { multiple: true },
      },
      {
        type: FieldType.Dropdown,
        copy: 'Ethnicity',
        key: 'demographics.ethnicity',
        options: ethnicities,
      },
    ],
  },
  // Covid-19 Impact Section
  [NHCustomQuestionKeys.FINANCIAL_HARDSHIP_STATEMENT]: {
    key: NHCustomQuestionKeys.FINANCIAL_HARDSHIP_STATEMENT,
    copy: {
      title: 'Has your household experienced financial hardship due to COVID-19?',
      intro: `
Please select your answer to the following statement:

“One or more individuals within my household qualified for unemployment benefits after March 13, 2020”`,
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: NHCustomQuestionKeys.FINANCIAL_HARDSHIP_STATEMENT,
        options: YesNoOptions,
      },
    ],
  },
  [NHCustomQuestionKeys.FINANCIAL_HARDSHIP_EXPERIENCE]: {
    key: NHCustomQuestionKeys.FINANCIAL_HARDSHIP_EXPERIENCE,
    copy: {
      title: 'Has your household experienced financial hardship due to COVID-19?',
      intro: 'Since March 2020, what significant expenses have you had due to COVID-19?',
    },
    dynamicLogic: eq('financialHardship.statement.qualifiedBenefits', 'no'),
    fields: [
      {
        type: FieldType.CheckboxGroup,
        key: 'financialHardship.experience.expenses',
        options: [
          {
            label: 'Increased food expenses or food delivery expenses',
            value: 'increasedFoodOrFoodDelivery',
          },
          {
            label: 'Increased utility, heating, or internet bills',
            value: 'increasedUtilityHeatingInternet',
          },
          { label: 'Increased transportation costs', value: 'increasedTransportationCosts' },
          {
            label: 'Increased childcare expenses due to school closures',
            value: 'increaseChildcareDueSchoolClosures',
          },
          {
            label: 'Air quality expenses - filters, ventilation',
            value: 'airQualityExpenses',
          },
          {
            label: 'Personal protective equipment, including masks, gloves, hand sanitizer',
            value: 'personalProtectiveEquipment',
          },
          { label: 'Remote or at-home work expenses', value: 'remoteOrAtHomeWorkExpenses' },
          {
            label: 'Payments made by credit card or payroll loan to avoid homelessness',
            value: 'paymentsByCreditOrPayrollLoan',
          },
          {
            label: 'Penalties, fees, and/or legal costs due to rental or utility past due amounts',
            value: 'costsDueRentalOrUtilitiesPastDueAmounts',
          },
          { label: 'None of the above', value: 'none' },
        ],
      },
      {
        type: FieldType.Typography,
        key: 'financialHardship.experience.eligibilityText',
        copy: 'If your household did not experience significant expenses or a reduction in income due to COVID-19 since March 2020, you may not be eligible for NHERAP assistance.',
        props: { variant: 'fieldLabel', alignment: 'center' },
      },
      {
        type: FieldType.Checkbox,
        key: 'financialHardship.experience.attestation',
        copy: 'I attest that the information I provided regarding the financial impact of COVID-19 on my household is correct and complete to the best of my knowledge.',
      },
    ],
  },
  [NHCustomQuestionKeys.FINANCIAL_HARDSHIP_INCOME]: {
    key: NHCustomQuestionKeys.FINANCIAL_HARDSHIP_INCOME,
    copy: {
      title: 'Has your household experienced financial hardship due to COVID-19?',
      intro: 'Since March 2020, how has COVID-19 affected your household’s income?',
    },
    dynamicLogic: and([
      eq('financialHardship.statement.qualifiedBenefits', 'no'),
      inList('financialHardship.experience.expenses', 'none'),
    ]),
    fields: [
      {
        type: FieldType.Typography,
        key: 'financialHardship.income.none',
        copy: 'You selected: None of the above.',
        props: { variant: 'body' },
      },
      {
        type: FieldType.CheckboxGroup,
        key: 'financialHardship.income.incomeReductions',
        options: [
          {
            label: 'Pay or hours reduced because of the impact of COVID on the business',
            value: 'payOrHoursReductionBecauseCovid',
          },
          { label: 'Sick with COVID and was unable to work', value: 'sickWithCovid' },
          {
            label: 'Loss of income due to caring for a household member sick with COVID',
            value: 'incomeLossDueCaringCovidSick',
          },
          { label: 'Loss of child or spousal support', value: 'lostChildOrSpousalSupport' },
          {
            label: 'Caring at home for children usually in school or daycare',
            value: 'caringChildrenAtHome',
          },
          {
            label: 'Loss of income after March 2020 but not because of COVID',
            value: 'incomeLossNotBecauseCovid',
          },
          {
            label: 'Caring for a household member sick with something other than COVID',
            value: 'caringHouseholdMemberSickNotCovid',
          },
          { label: 'None of the above', value: 'none' },
        ],
      },
      {
        type: FieldType.Typography,
        key: 'financialHardship.income.eligibilityText',
        copy: 'If your household did not experience significant expenses or a reduction in income due to COVID-19 since March 2020, you may not be eligible for NHERAP assistance.',
        props: { variant: 'fieldLabel', alignment: 'center' },
      },
      {
        type: FieldType.Checkbox,
        key: 'financialHardship.income.attestation',
        copy: 'I attest that the information I provided regarding the financial impact of COVID-19 on my household is correct and complete to the best of my knowledge.',
      },
    ],
  },
  [NHCustomQuestionKeys.HOUSING_INSTABILITY_RECEIVED_DOCUMENTS]: {
    key: NHCustomQuestionKeys.HOUSING_INSTABILITY_RECEIVED_DOCUMENTS,
    copy: {
      title: 'Have you experienced a risk of housing instability or homelessness?',
      intro: 'Have you received any of the following documents?',
    },
    fields: [
      {
        type: FieldType.CheckboxGroup,
        key: NHCustomQuestionKeys.HOUSING_INSTABILITY_RECEIVED_DOCUMENTS,
        options: [
          { value: 'utilityPastDue', label: 'Past due utility bill.' },
          { value: 'rentPastDue', label: 'Past due rent notice.' },
          { value: 'evictionNotice', label: 'Eviction notice.' },
          { label: 'None of the above', value: 'none' },
        ],
      },
    ],
  },
  [NHCustomQuestionKeys.HOUSING_INSTABILITY_STATEMENT]: {
    key: NHCustomQuestionKeys.HOUSING_INSTABILITY_STATEMENT,
    copy: {
      title: 'Have you experienced a risk of housing instability or homelessness?',
      intro: `
Please indicate if the following statement is true for you or any member of your household:

“I pay more than 30% of income for rent and utilities / I am at risk of homelessness.”`,
    },
    dynamicLogic: inList('housingInstability.receivedDocuments', 'none'),
    fields: [
      {
        type: FieldType.RadioList,
        key: NHCustomQuestionKeys.HOUSING_INSTABILITY_STATEMENT,
        options: RadioButtonOptions.YesNoUnsure,
      },
    ],
  },
  [NHCustomQuestionKeys.HOUSING_INSTABILITY_UNSAFE_CONDITIONS]: {
    key: NHCustomQuestionKeys.HOUSING_INSTABILITY_UNSAFE_CONDITIONS,
    copy: {
      title: 'Have you experienced a risk of housing instability or homelessness?',
      intro: 'Have you or a member of your household lived in unsafe or unhealthy conditions?',
    },
    dynamicLogic: and([
      inList('housingInstability.receivedDocuments', 'none'),
      {
        type: ExpressionType.Compare,
        op: CompareOperator.In,
        comparators: [
          literal(['no', 'unsure']),
          answer('housingInstability.statement.riskOfHomelessness'),
        ],
      },
    ]),
    fields: [
      {
        type: FieldType.CheckboxGroup,
        key: 'housingInstability.unsafeConditions.conditions',
        options: [
          {
            value: 'minimumStandards',
            label:
              'Housing does not meet minimum standards and local minimum requirements for use and occupancy, including NH RSA:48-A:14',
          },
          { value: 'hazards', label: 'Presence of mold or lead paint hazards' },
          { value: 'noUtilities', label: 'Utilities not in service' },
          { value: 'noHeat', label: 'No heat' },
          { value: 'codeViolations', label: 'City code notice of violation(s)' },
          {
            value: 'overcrowded',
            label: 'Overcrowded (more than two persons per bedroom) or staying with relatives',
          },
          { label: 'None of the above', value: 'none' },
        ],
      },
      {
        type: FieldType.Checkbox,
        key: 'housingInstability.unsafeConditions.attestation',
        copy: 'I attest that the information I provided regarding my household’s housing instability is correct and complete to the best of my knowledge.',
      },
    ],
  },
  // Documents Section
  [NHCustomQuestionKeys.APPLICATION_DOCUMENTS]: {
    key: NHCustomQuestionKeys.APPLICATION_DOCUMENTS,
    copy: {
      title: 'Document Upload',
      intro:
        'Please provide the following documents if you have them immediately available. If you do not, you can skip this step. Please note that program staff may reach out to you to ask for additional documentation.',
    },
    layout: 'panel',
    skippable: true,
    fields: [
      {
        type: FieldType.Document,
        key: 'documents.residency',
        validation: DOCUMENT_VALIDATION,
        copy: {
          title: 'Proof of Residency',
          description:
            'Examples include your lease, a confirmation from your landlord that you live at your stated address, or a bill with your name and address on it.',
        },
      },
      {
        type: FieldType.Document,
        key: 'documents.income',
        validation: DOCUMENT_VALIDATION,
        copy: {
          title: 'Proof of Income',
          description:
            'Examples include benefit determination letter(s) (WIC, SNAP, etc.), tax forms from prior tax years, recent paystubs, or other income-related documentation.',
        },
      },
      {
        type: FieldType.Document,
        key: 'documents.rentAmount',
        validation: DOCUMENT_VALIDATION,
        copy: {
          title: 'Proof of Rent Amount',
          description:
            'Examples include your lease, other proof of monthly rent, a notice of rent demand, other evidence of past due rent, or a W-9 from your landlord. Note: Program staff will ask your landlord for this documentation if you cannot provide it.',
        },
      },
      {
        type: FieldType.Document,
        key: 'documents.utilityBills',
        validation: DOCUMENT_VALIDATION,
        copy: {
          title: 'Current Utility Bills',
          description:
            'If you are requesting assistance with utilities, upload any current utility bills you have.',
        },
      },
      {
        type: FieldType.Document,
        key: 'documents.internetBills',
        validation: DOCUMENT_VALIDATION,
        copy: {
          title: 'Internet Bills',
          description:
            'If you are requesting internet assistance, upload your current internet bill.',
        },
      },
    ],
  },

  // Household Info Section
  [CommonQuestionKeys.HOUSEHOLD_SIZE]: {
    key: CommonQuestionKeys.HOUSEHOLD_SIZE,
    copy: { title: 'How many people live in your household?' },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Number,
        copy: 'Household Size',
        key: CommonQuestionKeys.HOUSEHOLD_SIZE,
        validation: {
          required: true,
          rules: [
            {
              rule: gte('householdSize', 1),
              message: 'Size cannot be lower than 1',
            },
          ],
        },
        props: { helperText: 'Example: 3' },
      },
    ],
  },
  [NHCustomQuestionKeys.HOUSEHOLD_INCOME]: {
    key: NHCustomQuestionKeys.HOUSEHOLD_INCOME,
    copy: {
      title: 'What is your household’s monthly income?',
      intro: 'Please provide your household’s combined monthly income.',
    },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Currency,
        copy: 'Monthly Household Income',
        key: 'householdIncome.monthlyAmount',
        validation: {
          required: true,
          rules: [
            {
              rule: gte('householdIncome.monthlyAmount', 0),
              message: 'Amount cannot be lower than 0',
            },
          ],
        },
      },
      {
        type: FieldType.Checkbox,
        key: 'householdIncome.attestation',
        copy: 'I attest that the information I provided for household income is correct and complete to the best of my knowledge.',
      },
      {
        type: FieldType.Calculated,
        key: 'householdIncome.income.annualized',
        formula: multiply('householdIncome.monthlyAmount', 12 * 100),
      },
    ],
  },
  [CommonQuestionKeys.HOUSEHOLD_MEMBERS]: {
    key: CommonQuestionKeys.HOUSEHOLD_MEMBERS,
    copy: { title: 'Who lives in your household?' },
    fields: [
      {
        type: FieldType.Complex,
        key: CommonQuestionKeys.HOUSEHOLD_MEMBERS,
        copy: 'Household Member',
        subFields: [
          {
            type: FieldType.Text,
            inputType: TextInputType.Text,
            copy: 'Name',
            key: 'name',
          },
          {
            type: FieldType.Date,
            copy: 'Date of Birth',
            key: CommonQuestionKeys.DATE_OF_BIRTH,
            validation: { required: true, allowedRange: 'past' },
          },
          {
            type: FieldType.Dropdown,
            copy: 'Gender',
            key: CommonQuestionKeys.GENDER,
            options: genders,
          },
          {
            type: FieldType.Dropdown,
            copy: 'Race',
            key: CommonQuestionKeys.RACE,
            options: races,
            props: { multiple: true },
          },
          {
            type: FieldType.Dropdown,
            copy: 'Ethnicity',
            key: CommonQuestionKeys.ETHNICITY,
            options: ethnicities,
          },
        ],
      },
    ],
  },
  [NHCustomQuestionKeys.HOUSEHOLD_MEMBERS_PUBLIC_BENEFITS]: {
    key: NHCustomQuestionKeys.HOUSEHOLD_MEMBERS_PUBLIC_BENEFITS,
    copy: {
      title: 'Did your household qualify for public benefits assistance?',
      intro:
        'Did anyone in your household qualify for any of the following benefit programs on January 1, 2020 or later? If so, you are income-eligible for NHERAP assistance. Check all that apply. Please note that you may be asked to provide your benefit letter(s).',
    },
    skippable: true,
    fields: [
      {
        type: FieldType.CheckboxGroup,
        key: 'publicBenefits.qualifiedBenefits',
        options: [
          { value: 'snap', label: 'SNAP (food stamps)' },
          { value: 'fanfTanf', label: 'FANF/TANF' },
          { value: 'ssi', label: 'SSI (not Social Security Retirement or Disability)' },
          { value: 'oaa', label: 'Old Age Assistance OAA' },
          { value: 'anb', label: 'Aid to the Needy Blind (ANB)' },
          { value: 'electricalAssistance', label: 'Electrical Assistance Program' },
          { value: 'wic', label: 'WIC' },
          { value: 'headStart', label: 'Head Start' },
          { value: 'fuelAssistance', label: 'Fuel Assistance (not all areas)' },
        ],
      },
    ],
  },
  // Submission/Agreements Section
  [NHCustomQuestionKeys.APPLICATION_RECEIVED_ASSISTANCE]: {
    key: NHCustomQuestionKeys.APPLICATION_RECEIVED_ASSISTANCE,
    copy: {
      title: 'Did someone help you fill out this application?',
      intro: `
Please indicate if the following statement is true:

“Someone helped me fill out this application.”`,
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: NHCustomQuestionKeys.APPLICATION_RECEIVED_ASSISTANCE,
        options: YesNoOptions,
      },
    ],
  },
  [CommonQuestionKeys.APPLICATION_ASSISTANCE_HELPER]: mergeObjects(
    [
      STANDARD_COMPONENTS.definitions.questions[CommonQuestionKeys.APPLICATION_ASSISTANCE_HELPER],
      {
        copy: { intro: 'Please complete the remaining fields below.' },
        skippable: false,
        dynamicLogic: eq('submission.assistance.receivedAssistance', 'yes'),
      } as ApplicationQuestion,
    ],
    'merge',
  ),
  [CommonQuestionKeys.SIGNATURE]: {
    key: CommonQuestionKeys.SIGNATURE,
    copy: {
      title: 'Submit Your Application',
      intro:
        'Please type your signature below confirming that you have read and understand each statement:',
    },
    layout: 'panel',
    fields: [
      {
        type: FieldType.Typography,
        key: 'submission.signature.agreements.header',
        copy: 'Agreements',
        props: { variant: 'h3', alignment: 'left' },
      },
      ...agreements.map(
        (copy: string, idx): Field => ({
          type: FieldType.Typography,
          key: `submission.signature.agreements.${idx}`,
          copy,
          props: { variant: 'body', alignment: 'left' },
        }),
      ),
      {
        type: FieldType.Checkbox,
        key: 'submission.signature.attestation',
        copy: `I have read and understand the above attestation (certification). I am
electronically signing this Application, Release of Information, and
Attestation (Certification) by entering my name and date below.`,
      },
      {
        type: FieldType.Text,
        key: 'submission.signature.signature',
        inputType: TextInputType.Signature,
        copy: 'Electronic Signature',
      },
      {
        type: FieldType.Date,
        key: 'submission.signature.date',
        copy: "Today's Date",
        validation: { required: true, allowedRange: 'today' },
      },
      {
        type: FieldType.Typography,
        key: 'submission.signature.nonDiscriminationPolicy',
        copy: '**Nondiscrimination Policy:** New Hampshire’s Emergency Rental Assistance Program prohibits discrimination based on race, color, creed, religion, sex, age, national origin, marital status, sexual orientation, familial status, and physical or mental disability.',
        props: { variant: 'body', alignment: 'left' },
      },
    ],
  },
  [NHCustomQuestionKeys.REQUESTS_RENTAL_ASSISTANCE]: {
    key: NHCustomQuestionKeys.REQUESTS_RENTAL_ASSISTANCE,
    copy: {
      title: 'Rental Assistance Request',
      intro:
        'Please enter your household’s monthly rental payment and the amount past due you owe your landlord for rent, utilities, and/or late fees from March 13, 2020 through the application date.',
    },
    skippable: true,
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Currency,
        copy: 'Monthly Rental Payment',
        key: 'requests.rentalAssistance.pastDueAmount',
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Currency,
        copy: 'Amount Past Due to Landlord',
        key: 'requests.rentalAssistance.monthlyRentAmount',
      },
    ],
  },
  [NHCustomQuestionKeys.REQUESTS_UTILITY_ASSISTANCE]: {
    key: NHCustomQuestionKeys.REQUESTS_UTILITY_ASSISTANCE,
    copy: {
      title: 'Utility Assistance Request',
      intro:
        'Enter the total amount you are requesting for each utility below. You can request up to 18 months of utility assistance dating back to March 13, 2020.',
    },
    skippable: true,
    fields: [
      {
        type: FieldType.Typography,
        key: 'utilityAssistance.pastDue',
        copy: 'Past Due Utilities',
        props: { variant: 'h2', alignment: 'left' },
      },
      {
        type: FieldType.Complex,
        key: 'requests.utilityAssistance.pastDue',
        copy: 'Past Due Utilities',
        useLabelPrefix: false,
        validation: { required: false },
        subFields: [
          {
            type: FieldType.Dropdown,
            copy: 'Utility',
            key: 'type',
            options: utilityTypes,
          },
          {
            type: FieldType.Text,
            inputType: TextInputType.Text,
            copy: 'Name of Utility Provider',
            key: 'provider',
          },
          {
            type: FieldType.Text,
            inputType: TextInputType.Text,
            copy: 'Account Number',
            key: 'accountNumber',
            validation: { required: false },
          },
          {
            type: FieldType.Text,
            inputType: TextInputType.Currency,
            copy: 'Total Amount Requested',
            key: 'amount',
          },
        ],
      },
      {
        type: FieldType.Typography,
        key: 'utilityAssistance.current',
        copy: 'Current Utility Bills',
        props: { variant: 'h2', alignment: 'left' },
      },
      {
        type: FieldType.Complex,
        key: 'requests.utilityAssistance.current',
        copy: 'Current Utility Bills',
        useLabelPrefix: false,
        validation: { required: false },
        subFields: [
          {
            type: FieldType.Dropdown,
            copy: 'Utility',
            key: 'type',
            options: utilityTypes,
          },
          {
            type: FieldType.Text,
            inputType: TextInputType.Text,
            copy: 'Name of Utility Provider',
            key: 'provider',
          },
          {
            type: FieldType.Text,
            inputType: TextInputType.Text,
            copy: 'Account Number',
            key: 'accountNumber',
            validation: { required: false },
          },
          {
            type: FieldType.Text,
            inputType: TextInputType.Currency,
            copy: 'Total Amount Requested',
            key: 'amount',
          },
        ],
      },
    ],
  },
  [NHCustomQuestionKeys.REQUESTS_OTHER_EXPENSES]: {
    key: NHCustomQuestionKeys.REQUESTS_OTHER_EXPENSES,
    copy: {
      title: 'Other Housing Expenses',
      intro:
        'Please enter any other expenses that you need assistance with, such as internet or relocation costs.',
    },
    skippable: true,
    fields: [
      {
        type: FieldType.Complex,
        key: 'requests.otherExpenses.expenses',
        copy: 'Other Expense',
        useLabelPrefix: false,
        subFields: [
          {
            type: FieldType.Text,
            inputType: TextInputType.Text,
            copy: 'Name of Provider',
            key: 'expenseProvider',
          },
          {
            type: FieldType.Text,
            inputType: TextInputType.Currency,
            copy: 'Total Amount Requested',
            key: 'amount',
          },
        ],
      },
    ],
  },
};

const SECTIONS: ApplicationSection[] = [
  {
    key: SpecialSectionKeys.AdminOnly,
    // TODO: Allow sections without overview only if they're admin-only
    overview: null,
    name: null,
    questionGroups: [
      {
        key: 'adminOnly',
        name: null,
        questions: [QUESTIONS.adminOnly],
      },
    ],
  },
  {
    key: 'personalInfo',
    name: 'Personal Info',
    overview: {
      description: `
In this first section, you’ll share some basic personal information, including:

- Date of birth
- Gender, race, and ethnicity`,
      title: 'Let’s start by gathering your personal information',
    },
    questionGroups: [
      {
        key: 'personalInfo',
        name: 'Personal Info',
        questions: [
          QUESTIONS[CommonQuestionKeys.DATE_OF_BIRTH],
          QUESTIONS[CommonQuestionKeys.DEMOGRAPHICS],
        ],
      },
    ],
  },
  {
    key: 'addressAndLandlordInfo',
    name: 'Address & Landlord',
    overview: {
      description: `
In this section, you’ll enter your address, mailing address (if different), and
your landlord’s contact information.`,
      title: 'Now, let’s get your address and landlord information',
    },
    questionGroups: [
      {
        key: 'address',
        name: 'Your Current Address',
        questions: [QUESTIONS[CommonQuestionKeys.ADDRESS]],
      },
      {
        key: 'mailingAddress',
        name: 'Your Mailing Address',
        questions: [QUESTIONS[CommonQuestionKeys.MAILING_ADDRESS]],
        dynamicLogic: eq('address.address.isUnhoused', false),
      },
      {
        key: 'landlord',
        name: 'Landlord Contact Info',
        questions: [QUESTIONS[NHCustomQuestionKeys.LANDLORD]],
        dynamicLogic: eq('address.address.isUnhoused', false),
      },
    ],
  },
  {
    key: 'householdInfo',
    name: 'Household Info',
    overview: {
      description: `
In this part of the application, you’ll share information about your household, including:

- Your household size and members
- Your household’s combined monthly income
- Whether any member of your household qualified for public benefits assistance`,
      title: 'Now, let’s gather information about your household',
    },
    questionGroups: [
      {
        key: 'size',
        name: 'Household Size',
        questions: [QUESTIONS[CommonQuestionKeys.HOUSEHOLD_SIZE]],
      },
      {
        key: CommonQuestionKeys.HOUSEHOLD_MEMBERS,
        name: 'Household Members',
        questions: [QUESTIONS[CommonQuestionKeys.HOUSEHOLD_MEMBERS]],
      },
      {
        key: NHCustomQuestionKeys.HOUSEHOLD_INCOME,
        name: 'Household Income',
        questions: [QUESTIONS[NHCustomQuestionKeys.HOUSEHOLD_INCOME]],
      },
      {
        key: NHCustomQuestionKeys.HOUSEHOLD_MEMBERS_PUBLIC_BENEFITS,
        name: 'Public Benefits Assistance',
        questions: [QUESTIONS[NHCustomQuestionKeys.HOUSEHOLD_MEMBERS_PUBLIC_BENEFITS]],
      },
    ],
  },
  {
    key: 'covidImpact',
    name: 'Covid-19 Impact',
    overview: {
      description: `
In this section, you’ll let us know if your household has experienced financial hardship
and housing instability due to COVID-19.`,
      title: 'Now, let’s learn about how COVID-19 has impacted your household',
    },
    questionGroups: [
      {
        key: 'financialHardship',
        name: 'Financial Hardship',
        questions: [
          QUESTIONS[NHCustomQuestionKeys.FINANCIAL_HARDSHIP_STATEMENT],
          QUESTIONS[NHCustomQuestionKeys.FINANCIAL_HARDSHIP_EXPERIENCE],
          QUESTIONS[NHCustomQuestionKeys.FINANCIAL_HARDSHIP_INCOME],
        ],
      },

      {
        key: 'housingInstability',
        name: 'Housing Instability',
        questions: [
          QUESTIONS[NHCustomQuestionKeys.HOUSING_INSTABILITY_RECEIVED_DOCUMENTS],
          QUESTIONS[NHCustomQuestionKeys.HOUSING_INSTABILITY_STATEMENT],
          QUESTIONS[NHCustomQuestionKeys.HOUSING_INSTABILITY_UNSAFE_CONDITIONS],
        ],
      },
    ],
  },
  {
    key: 'assistanceRequests',
    name: 'Requests for Assistance',
    overview: {
      description: `
In this section, you’ll let us know what you need assistance with. You can request
assistance with rent, past and current utilities, and/or other housing expenses.`,
      title: 'Let’s calculate your requests for assistance',
    },
    questionGroups: [
      {
        key: 'rentalAssistance',
        name: 'Rental Assistance Request',
        questions: [QUESTIONS[NHCustomQuestionKeys.REQUESTS_RENTAL_ASSISTANCE]],
      },
      {
        key: 'utilityAssistance',
        name: 'Utility Assistance Request',
        questions: [QUESTIONS[NHCustomQuestionKeys.REQUESTS_UTILITY_ASSISTANCE]],
      },
      {
        key: 'otherExpenses',
        name: 'Other Housing Expenses',
        questions: [QUESTIONS[NHCustomQuestionKeys.REQUESTS_OTHER_EXPENSES]],
      },
    ],
  },
  {
    key: NHCustomQuestionKeys.APPLICATION_DOCUMENTS,
    name: 'Document Upload',
    questionGroups: [
      {
        key: 'documentUpload',
        name: 'Documents Uploaded',
        questions: [QUESTIONS[NHCustomQuestionKeys.APPLICATION_DOCUMENTS]],
      },
    ],
    overview: {
      title: 'Document Upload',
      description: `
You will be asked to upload documentation supporting your answers to the different
sections of the application. This is not a required step as you are able to submit your
application without uploading documentation. Please note that a member of our staff may
reach out to you and ask for documentation after you submit your application.`,
    },
  },
  mergeObjects(
    [
      STANDARD_COMPONENTS.definitions.sections.review,
      {
        questionGroups: [
          {
            questions: [
              QUESTIONS[NHCustomQuestionKeys.APPLICATION_RECEIVED_ASSISTANCE],
              QUESTIONS[CommonQuestionKeys.APPLICATION_ASSISTANCE_HELPER],
            ],
          },
          {
            key: CommonQuestionKeys.SIGNATURE,
            name: 'Agreements',
            questions: [QUESTIONS[CommonQuestionKeys.SIGNATURE]],
          },
        ],
      } as ApplicationSection,
    ],
    'merge',
  ),
];

const CONFIG: ApplicationConfig = {
  introPages: [
    {
      key: 'what-is-it',
      layout: 'welcome',
      title: 'What is emergency rental assistance?',
      body: `
The COVID-19 pandemic has made it difficult for many tenants to cover rent, utilities, and 
other expenses every month.

If you are a tenant struggling with these expenses or a landlord with tenants behind on rent, 
you may be eligible to receive financial assistance through {PROGRAM_NAME}.`,
    },
    {
      key: 'who-is-eligible',
      layout: 'steps',
      title: 'Who is eligible for emergency rental assistance?',
      steps: [
        {
          title: 'Households affected by COVID-19',
          icon: 'checkCircleOutline',
          description:
            'If you or any member of your household lost work or significant income due to the pandemic, you’re in the right place.',
        },
        {
          title: 'Households facing housing instability',
          icon: 'checkCircleOutline',
          description:
            'If you or any member of your household is at risk of experiencing housing instability or homelessness, you’re in the right place.',
        },
        {
          title: 'Lower income households',
          icon: 'checkCircleOutline',
          description:
            'If your household income is at or below 80% of the Area Median Income, you’re in the right place.',
        },
        {
          title: 'Landlords',
          icon: 'checkCircleOutline',
          description:
            'If your tenants meet the criteria above, you are eligible to apply for rental assistance.',
        },
      ],
    },
    {
      key: 'how-do-i-get-it',
      layout: 'steps',
      title: 'How do I get emergency rental assistance?',
      steps: [
        {
          title: 'Spend 10 minutes or less filling out our application',
          icon: 'time',
        },
        {
          title: 'Receive assistance with rent, utilities, and/or other housing expenses',
          icon: 'money',
          description:
            'If your application is approved, we will send payment to your landlord, utility provider, and/or other providers.',
        },
      ],
    },
  ],
  overrides: {
    statuses: [
      {
        status: 'Application',
        message:
          'You could be eligible for up to 18 months of aid on past and future rent, utility payments, and other expenses as part of NHERAP. You must be 18+ years or older and a resident of New Hampshire. Citizenship and immigration status does not affect eligibility.',
      },
      {
        status: 'Approved',
        message:
          'Congratulations! We are pleased to inform you that your application for Emergency Assistance has been approved for funding. We will be reaching out to your landlord, utility company and/or other vendors on your behalf for payment. If you have any questions please reach out at rap@snhs.org.',
      },
      {
        status: 'Denied',
        message:
          'Thank you for your interest in NHERAP. Unfortunately, you do not meet the eligibility criteria for this program. You may be eligible to reapply after three months from your decision date. View more details below.',
      },
      {
        status: 'InReview',
        message:
          'A case manager is reviewing your application and verifying your eligibility for assistance. Please check back here for updates.',
      },
    ],
  },
  sections: SECTIONS,
};
export default CONFIG;
